import React, { useMemo, useState } from 'react';
import { consoleTokens } from '@components/ui/console';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { Box, InputBase, Popover, Stack, Typography } from '@mui/material';

import type { ReportColumn } from '../types';

const GROUP_LABELS: Record<string, string> = {
  context: 'Context',
  identity: 'Identity',
  demographics: 'Demographics',
  contact: 'Contact',
  address: 'Addresses',
  emergency_contact: 'Emergency contact',
  employment: 'Employment',
  insurance: 'Insurance',
  administrative: 'Administrative',
  appointment: 'Appointment',
  delivered: 'Delivered',
  compliance: 'Compliance',
  collected: 'Collected',
  measure: 'Measures',
};

export interface ColumnsPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Full detail-column catalog, in definition order. */
  allColumns: ReportColumn[];
  /** Currently selected keys (definition order). */
  selected: string[];
  defaultColumns: string[];
  /** null resets to the report default. */
  onChange: (next: string[] | null) => void;
}

/**
 * The `columns:` token editor — a dense, searchable toggle list grouped by
 * the backend's column groups. Selection order is definition order (no
 * reordering UI; predictability over flexibility).
 */
export const ColumnsPopover: React.FC<ColumnsPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  allColumns,
  selected,
  defaultColumns,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? allColumns.filter(column => column.label.toLowerCase().includes(needle))
      : allColumns;
  }, [allColumns, query]);

  const toggle = (key: string) => {
    const next = allColumns
      .map(column => column.key)
      .filter(columnKey =>
        columnKey === key ? !selectedSet.has(columnKey) : selectedSet.has(columnKey),
      );
    if (next.length === 0) return; // never zero columns
    onChange(next);
  };

  let lastGroup: string | null = null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setQuery('');
        onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 300 } } }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          borderBottom: `1px solid ${consoleTokens.line}`,
          height: 40,
          px: 1.5,
        }}
      >
        <SearchIcon sx={{ color: consoleTokens.fainter, fontSize: 16 }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Filter columns…"
          value={query}
          onChange={event => setQuery(event.target.value)}
          inputProps={{ 'aria-label': 'Filter columns' }}
          sx={{ fontSize: '0.75rem' }}
        />
        <Typography
          sx={{
            color: consoleTokens.fainter,
            fontFamily: consoleTokens.mono,
            fontSize: '0.6875rem',
          }}
        >
          {selected.length}
        </Typography>
      </Stack>

      <Box sx={{ maxHeight: 360, overflowY: 'auto', py: 0.5 }}>
        {visible.map(column => {
          const showGroup = column.group !== lastGroup && !query;
          lastGroup = column.group;
          const isSelected = selectedSet.has(column.key);
          return (
            <React.Fragment key={column.key}>
              {showGroup && (
                <Typography
                  sx={{
                    color: consoleTokens.fainter,
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    pb: 0.25,
                    pt: 1,
                    px: 1.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {GROUP_LABELS[column.group] ?? column.group}
                </Typography>
              )}
              <Stack
                direction="row"
                onClick={() => toggle(column.key)}
                sx={{
                  alignItems: 'center',
                  cursor: 'pointer',
                  gap: 1,
                  height: 28,
                  px: 1.5,
                  '&:hover': { bgcolor: consoleTokens.bandBg },
                }}
              >
                <Box sx={{ width: 16 }}>
                  {isSelected && <CheckIcon sx={{ color: consoleTokens.ink, fontSize: 14 }} />}
                </Box>
                <Typography
                  noWrap
                  sx={{
                    color: isSelected ? consoleTokens.ink : consoleTokens.dim,
                    fontSize: '0.75rem',
                  }}
                >
                  {column.label}
                </Typography>
              </Stack>
            </React.Fragment>
          );
        })}
        {visible.length === 0 && (
          <Typography sx={{ color: consoleTokens.dim, fontSize: '0.75rem', px: 1.5, py: 2 }}>
            No matching columns.
          </Typography>
        )}
      </Box>

      <Stack direction="row" sx={{ borderTop: `1px solid ${consoleTokens.line}` }}>
        <Box
          component="button"
          type="button"
          onClick={() => onChange(null)}
          sx={{
            bgcolor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            flex: 1,
            px: 1.5,
            py: 1,
            textAlign: 'left',
            '&:hover': { bgcolor: consoleTokens.bandBg },
          }}
        >
          <Typography
            sx={{ color: consoleTokens.dim, fontFamily: consoleTokens.mono, fontSize: '0.6875rem' }}
          >
            Reset to default ({defaultColumns.length})
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => onChange(allColumns.map(column => column.key))}
          sx={{
            bgcolor: 'transparent',
            border: 'none',
            borderLeft: `1px solid ${consoleTokens.line}`,
            cursor: 'pointer',
            px: 1.5,
            py: 1,
            textAlign: 'right',
            '&:hover': { bgcolor: consoleTokens.bandBg },
          }}
        >
          <Typography
            sx={{ color: consoleTokens.dim, fontFamily: consoleTokens.mono, fontSize: '0.6875rem' }}
          >
            Select all ({allColumns.length})
          </Typography>
        </Box>
      </Stack>
    </Popover>
  );
};
